import {getRandomColor} from "../../../utils/GetRandomColor";
import {MathJax, MathJaxContext} from "better-react-mathjax";
export default function Basics() {
    const imgStyle = {
        backgroundColor: "#E9E8E6",
        border: `5px solid ${getRandomColor()}`,
    }

    const altTextStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        display: "block",
        textAlign: "center",
        marginTop: "-10px",
        fontStyle: "italic",
    }

    const mathJaxConfig = {
        loader: {load: ["[tex]/html"]},
        tex: {
            packages: {"[+]": ["html"]},
            inlineMath: [["$", "$"], ["\\(", "\\)"]],
            displayMath: [["$$", "$$"], ["\\[", "\\]"]]
        }
    }

    return (
        <MathJaxContext config={mathJaxConfig}>
            <div className={"blog-body"}>
            <h2>Basics</h2>
            <p>
                Mathematics and programming are like two peas in a pod. Computer algorithms cannot be
                constructed without mathematical thinking. Therefore, a significant level of mathematical
                maturity is required to understand how machine learning algorithms work. The topics in
                this chapter are considered basics because the computations can be performed using basic
                mathematical methods.
            </p>

            <h3>Geometry</h3>
            <p>
                Since data with multiple dimensions can be represented in a Euclidean space, one of the
                measurement methods used in machine learning is Euclidean distance. Distances between
                data points are used for similarity comparison, clustering, dimensionality reduction, etc.</p><p>
            Euclidean distance formula for n-dimensions is</p>

                <MathJax>{'\\begin{align}\nd(A,B) = \\sqrt{(a_1 - b_1) ^ 2 + (a_2 - b_2) ^ 2 + \\ldots +(a_n - b_n)^2}\n\\end{align}'}</MathJax>

                where <MathJax inline>{'$A=(a_1, a_2, \\ldots , a_n)$'}</MathJax> and <MathJax inline>{'$B=(b_1, b_2, \\ldots , b_n)$'}</MathJax>

            <p>The center of multiple data points in a space is called the centroid, and the formula for the
                centroid of <MathJax inline>{'$n$'}</MathJax> points is used for clustering, which is</p>

                <MathJax>{'\\begin{align}\n\\left(\\frac{x_1 + x_2 +...+x_n}{n},\\frac{y_1 + y_2 +...+y_n}{n}\\right)\n\\end{align}'}</MathJax>
            

            <h3>Metrics</h3>
            <p>
                The performance of a machine learning algorithm is measured by different metrics depending on
                the
                objective. The metrics used for regression and classification problems differ from each other.
                Some
                of the regression metrics are explained below.
            </p>

            <h4>Mean Squared Error</h4>
            <p>
                Mean squared error (MSE) is used to measure the sum of the distances between the data points and
                the
                regression line. Since the goal of a regression is to fit a line to the data, lower values of
                mean
                squared error mean that the model is performing better. The formula of the means squared error
                is
            </p>

                <MathJax>{'\\begin{align}\n\\frac{1}{n} \\sum_{i = 1}^{n}\\left(x_{i}-\\hat{x}_{i}\\right)^{2}\n\\end{align}'}</MathJax>

                where <MathJax inline>{'$n=\\text{number of data points}$'}</MathJax>, <MathJax inline>{'$x_{i}=\\text{observations}$'}</MathJax> and
                <MathJax inline>{'$\\hat{x}_{i}=\\text{predictions}$'}</MathJax>.

            <h4>Root Mean Squared Error</h4>
            <p>
                Mean absolute error (MAE) is a measurement method that works like mean squared error, but
                instead of
                summing the square root of the differences between predictions and observations, the absolute
                values
                of the differences are summed. The formula is as follows
            </p>

                <MathJax>{'\\begin{align}\n\\frac{\\sum_{i=1}^{n}\\left|x_{i}-\\hat{x}_{i}\\right|}{n}\n\\end{align}'}</MathJax>

            <h4>R-squared</h4>
            <p>
                <MathJax inline>{'$R^2$'}</MathJax>, also called coefficient of determination, is a measurement method for explaining the
                variance
                between observed and predicted values by the model.

                <MathJax inline>{'$R^2$'}</MathJax> is in the range of <MathJax inline>{'$[0,1]$'}</MathJax>, where <MathJax inline>{'$R^2=0$'}</MathJax> means that no variability is described by the
                regression, and <MathJax inline>{'$R^2=1$'}</MathJax> means that all variability is described by the regression. The <MathJax inline>{'$R^2$'}</MathJax> is
                usually observed between <MathJax inline>{'$(0.2,0.9)$'}</MathJax>. Note that the value of the <MathJax inline>{'$R^2$'}</MathJax> varies depending on the
                case,
                there is no strict interval for a good <MathJax inline>{'$R^2$'}</MathJax>.

                To calculate the <MathJax inline>{'$R^2$'}</MathJax>, some terms need to be clarified.
            </p>

            <h5>Sum of Squares Total</h5>
            <p>
                The sum of squares total (SST or TSS) is the sum of the differences between the observations and
                the
                mean. It indicates the total variability. The formula is
            </p>

                <MathJax>{'\\begin{align}\n\\sum_{i = 1}^{n} (x_i - \\bar{x})^2\n\\end{align}'}</MathJax>

            <h5>Sum of Squares Regression</h5>
            <p>
                The sum of squares regression (SSR or ESS) is the sum of the differences between the predictions
                and
                the mean. It indicates the explained variability. The formula is
            </p>

                <MathJax>{'\\begin{align}\n\\sum_{i = 1}^{n} (\\hat{x_i} - \\bar{x})^2\n\\end{align}'}</MathJax>

            <h5>Sum of Squares Error</h5>
            <p>
                The sum of squares error (SSE or RSS) is the sum of the difference between the observations and
                the
                predictions. It indicates the unexplained variability. The formula is as follows
            </p>

                <MathJax>{'\\begin{align}\n\\sum_{i = 1}^{n} (\\hat{x_i} - {x}_{i})^2\n\\end{align}'}</MathJax>

            <p>The sum of squares total (SST) is the addition of the sum of squares regression (SSR) and the
                sum of
                squares error (SSE), which means that the total variability is the sum of explained variability
                and
                unexplained variability.</p>

                <MathJax>{'\\begin{align}\n\\sum_{i = 1}^{n} (x_i - \\bar{x})^2 = \\sum_{i = 1}^{n} (\\hat{x_i} - \\bar{x})^2 + \\sum_{i = 1}^{n}\n(\\hat{x_i} - {x}_{i})^2\n\\end{align}'}</MathJax>

            <p>And the <MathJax inline>{'$R^2$'}</MathJax> can be calculated via the following formula</p>

                <MathJax>{'\\begin{align}\nR^2=\\frac{\\text{SSR}}{\\text{SST}}\n\\end{align}'}</MathJax>

            <h4>Adjusted R-squared</h4>
            <p>
                Adjusted R-squared, abbreviated as <MathJax inline>{'$\\bar{R}^2$'}</MathJax>, is a measurement method derived from <MathJax inline>{'$R^2$'}</MathJax>. It
                incorporates the sample size and the number of independent variables in addition to <MathJax inline>{'$R^2$'}</MathJax> and is
                calculated using the following formula.</p>

                <MathJax>{'\\begin{align}\n\\bar{R}^2 = 1-(1-R^2)\\frac{n - 1}{n - p - 1}\n\\end{align}'}</MathJax>

            <p>Where <MathJax inline>{'$n$'}</MathJax> is the sample size, which can also be viewed as the number of rows, and <MathJax inline>{'$p$'}</MathJax> is the
                number
                of independent variables, which indicates the number of features. Since the <MathJax inline>{'$\\bar{R}^2$'}</MathJax>
                penalizes
                the excessive use of variables, it is always smaller than the <MathJax inline>{'$R^2$'}</MathJax>.</p>

                <MathJax>{'\\begin{align}\n\\bar{R}^2 < R^2 \\end{align}'}</MathJax> 
            
            <p>And some of the classification metrics are explained below. </p>

            <h4>Accuracy, Precision and Recall</h4>
            <p>
                Accuracy, precision, and recall are the
                classification metrics calculated using the confusion matrix. The confusion matrix is a
                graph that shows the correct and incorrect predictions of the model. The rows show the
                predictions, while the columns show the observations. See Table 2 for clarification.</p>
                
                <center>
                    <table>
                        <thead>
                        <tr>
                            <th></th>
                            <th>Positive observations</th>
                            <th>Negative observations</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <th>Positive predictions</th>
                            <td>True positives (TP)</td>
                            <td>False positives (FP)</td>
                        </tr>
                        <tr>
                            <th>Negative predictions</th>
                            <td>False negatives (FN)</td>
                            <td>True negatives (TN)</td>
                        </tr>
                        </tbody>
                    </table>
                    <b>Table 2:</b> The confusion matrix.
                </center>
                

                <MathJax>{'\\begin{align}\n\\text{Accuracy}=\\frac{TP + TN}{TP + TN + FP + FN}=\\frac{\\text{correct predictions}}{\\text{all predictions}}\n\\end{align}'}</MathJax>
            <p>Accuracy is the ratio of total correct predictions to all predictions.</p>

            <p>The problem with accuracy is with unevenly distributed data sets. The measurement of
                accuracy is
                inadequate when the negative or positive observations make up the bulk of the data. For
                example,
                consider an observation set consisting of the following elements.</p>

                <pre><code className="language-python">observations = [A, A, A, A, A, A, A, A, B]</code></pre>

            <p>If a model makes a prediction consisting entirely of A's,</p>

                <pre><code className="language-python">predictions = [A, A, A, A, A, A, A, A, A]</code></pre>

            <p>the accuracy would be <MathJax inline>{'$90\\%$'}</MathJax>. The model completely ignores one of the observations and still
                achieves very high accuracy.</p>

            <p>The formula of the precision is</p>

                <MathJax>{'\\begin{align}\n\\text{Precision}=\\frac{TP}{TP + FP}=\\frac{\\text{true positives}}{\\text{all positive predictions}}\n\\end{align}'}</MathJax>

            <p>If the positive predictions are more important for the goal, it is better to use precision
                as a
                measure.</p>

            <p>And the formula of the recall is</p>

                <MathJax>{'\\begin{align}\n\\text{Recall}=\\frac{TP}{TP + FN}=\\frac{\\text{true positives}}{\\text{all positive observations}}\n\\end{align}'}</MathJax>

            <p>
                If the negative predictions are more important for the objective, recall is a better
                measurement
                method. To avoid the misconceptions caused by the unbalanced data sets, F1-score is used.
            </p>

            <h4>F1-score</h4>
            <p>
                The F1-score is a classification metric calculated based on precision and recall. In real-world
                machine learning applications, the data sets encountered are often unbalanced. The F1-score is
                used
                to remove the misunderstandings caused by the imbalanced data by averaging the precision and
                recall.
                The F1-score is calculated as follows.
            </p>

                <MathJax>{'\\begin{align}\nF_{1}=\\frac{2\\cdot\\text{precision}\\cdot\\text{recall}}{\\text{precision}+\\text{recall}}\n\\end{align}'}</MathJax>
            </div>
        </MathJaxContext>
    )
}